import React, { useState } from "react";
import axios, { getAxiosParams } from "src/lib/axios.config";
import { TemplateAndOverrides, TemplatesApi } from "typescript-axios";
import {
  Box,
  Button,
  HStack,
  useDisclosure,
  Spinner,
  StackDivider,
  VStack,
  useToast,
  Text,
} from "@chakra-ui/react";


import {
  ReactFlow,
  Controls,
  Background,
  Panel,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import { FaRocket } from "@react-icons/all-files/fa/FaRocket";
import { MdShare } from "@react-icons/all-files/md/MdShare";
import {
  createCustomNodes,
  getEdgesAndNodesFromJson,
  getUiSchemaForTemplate,
} from "src/appUtils";
import Modal from "src/components/Modal";
import useCurrentUser from "src/components/useCurrentUser";
import JSONForm, { toastPromise } from "src/JSONForm";
import { readNodeTypes } from "src/flowActions";
import NameDescPanel from "src/name-desc-panel";
import SubscribeModal from "src/studio/SubscribeModal";
import { canCreateNewFlow } from "src/studio/SubscribeModalUtils";
import { usePageContext } from "vike-react/usePageContext";
import { navigate } from "vike/client/router";
import OAuthModalInfo from "./oauth";
const TEMPLATES_API = new TemplatesApi(getAxiosParams(), undefined, axios);

// inherit from TemplateAndOverrides
interface TemplateAndOverridesAndHasAny extends TemplateAndOverrides {
  hasAnyOverride: boolean;
}

interface ExploreEditProps {
  root: string;
  onClick?: () => void;
}

const ExploreEdit = ({ root }) => {
  const { routeParams: { id: templateId } } = usePageContext();
  const [templateAndOverrides, setTemplateAndOverrides] =
    useState<TemplateAndOverridesAndHasAny | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [autoCreateGoogleSheets, setAutoCreateGoogleSheets] = useState(true);
  const [googleSheetsNodeIds, setGoogleSheetsNodeIds] = useState([]);
  const [customNodes, setCustomNodes] = useState({});
  const [uiSchema, setUiSchema] = React.useState({});
  const [orguiSchema, setOrgUiSchema] = React.useState({});
  const [initialData, setInitialData] = React.useState({});
  const [oauthUrl, setOauthUrl] = React.useState<{
    url: string | null;
    message?: string | null;
  }>({ url: null });
  const [isOwner, setIsOwner] = React.useState(false)

  const { data: currentUser } = useCurrentUser(false);

  const { urlParsed } = usePageContext();

  React.useEffect(() => {
    if (!currentUser || !templateAndOverrides) return;
    setIsOwner(currentUser.id === templateAndOverrides?.template.owner_id)
  }, [currentUser, templateAndOverrides])
  const toast = useToast();

  const createSheets = () => {
    // do something then
    setIsLoading(true);
    toastPromise(TEMPLATES_API.createGsheetsApiV1TemplatesTemplateIdSheetsPost(
      templateId,
      window.location.origin + "/auth/scopes",
    ).then((r) => {
      if (r.data.success && r.data.spreadsheet) {
        Object.entries(r.data.spreadsheet.sheet_names).forEach(([nodeId, sheetName]) => {
          uiSchema[nodeId]["sheet_id"] = { ...(uiSchema[nodeId]["sheet_id"] || {}), "ui:disabled": true };
          uiSchema[nodeId]["range_name"] = { ...(uiSchema[nodeId]["range_name"] || {}), "ui:disabled": true };
          uiSchema[nodeId]["ui:widget"] = "hidden";
          initialData[nodeId] = { ...(initialData[nodeId] || {}), range_name: sheetName, sheet_id: r.data.spreadsheet?.spreadsheet_id };
        })
        setUiSchema(uiSchema);
        setAutoCreateGoogleSheets(false);

        // if there were no other settings to provide we should create here
        const hasOtherOverride = Object.entries(templateAndOverrides?.overrides || {}).some(([nodeId, o]) => !Object.entries(o.properties).every(([k, v]) => v.default || initialData[nodeId]?.[k]));
        if (!hasOtherOverride) {
          createFlowFromTemplate(templateId, initialData);
        }

      } else {
        setOauthUrl({
          url: r.data.url,
          message:
            "You need to connect your Google Sheets account in order to do this.",
        })
      }

    }).finally(() => setIsLoading(false)), toast, {
      success: "Google Sheets created successfully",
    });
  }

  React.useEffect(() => {
    if (templateAndOverrides?.overrides) {
      const uiSchema = getUiSchemaForTemplate(templateAndOverrides.overrides)
      setOrgUiSchema(uiSchema);
      setUiSchema(JSON.parse(JSON.stringify(uiSchema)));
      setGoogleSheetsNodeIds(Object.entries(templateAndOverrides?.overrides || {}).filter(([nodeId, o]) => o["auto:create_gsheet"] && !o.properties["sheet_id"]['default']).map(([nodeId, o]) => nodeId))
    }
  }, [templateAndOverrides]);

  const {
    isOpen: isSubscribeOpen,
    onOpen: onSubscribeOpen,
    onClose: onSubscribeClose,
  } = useDisclosure();

  const {
    isOpen: isOverrideOpen,
    onOpen: onOverrideOpen,
    onClose: onOverrideClose,
  } = useDisclosure();

  const createFlowFromTemplate = (templateId: string, formData) => {
    return TEMPLATES_API.createFlowFromTemplateApiV1TemplatesTemplateIdFlowsPost(
      templateId,
      formData
    ).then((r) => {
      navigate(`/studio/${r.data.id}`);
    });
  };

  React.useEffect(() => {
    readNodeTypes().then((nodeTypes) => {
      setCustomNodes(createCustomNodes({ nodeTypes, isDemo: true }));
    });
  }, []);

  const onBuildClick = React.useCallback(() => {
    if (currentUser) {
      canCreateNewFlow().then((canCreate) => {
        if (canCreate) {
          if (templateAndOverrides?.hasAnyOverride) {
            onOverrideOpen();
          } else {
            createFlowFromTemplate(templateId, {});
          }
        } else {
          onSubscribeOpen();
        }
      });
    } else {
      navigate(`/auth?next=${urlParsed.pathname.replace("/gallery", "/explore")}`)
    }

  }, [templateAndOverrides]);

  React.useEffect(() => {
    if (templateId) {
      TEMPLATES_API.readTemplateApiV1TemplatesTemplateIdGet(templateId)
        .then((r) => {
          const hasAnyOverride = Object.values(r.data.overrides).reduce(
            (acc, v) => {
              return acc || (v && Object.keys(v.properties).length > 0);
            },
            false
          );
          setTemplateAndOverrides({ ...r.data, hasAnyOverride });
          const [edges, nodes] = getEdgesAndNodesFromJson(
            r.data.template.template_json || {}
          );

          setNodes(nodes);
          setEdges(edges);
        })
        .catch((e) => {
          if (e.response?.status === 404) {
            console.error(`Template not found ${templateId}`);
            navigate(root);
          } else {
            throw e;
          }
        });
    }
  }, [templateId]);

  if (templateAndOverrides === null) {
    return <Spinner />;
  }

  const UseTemplateButton = () => {
    return <Button
      id="test-btn"
      isDisabled={isLoading}
      size="sm"
      leftIcon={<FaRocket />}
      onClick={onBuildClick}
      colorScheme="brand"
    >
      Use this template
    </Button>
  }

  return (
    <Box h="full" bg="white">
      <OAuthModalInfo
        oauthUrl={oauthUrl.url}
        message={oauthUrl.message}
        onClose={() => setOauthUrl({ url: null })}
      />
      <Modal
        header="Use this template"
        isOpen={isOverrideOpen}
        size="xl"
        onClose={() => {
          setAutoCreateGoogleSheets(true)
          setInitialData({})
          // force reset uiSchema
          setUiSchema(JSON.parse(JSON.stringify(orguiSchema)));
          onOverrideClose()
        }}
      >
        <VStack align='stretch'>
          {templateAndOverrides?.hasAnyOverride && (
            <>
              <Text>
                This template has parameters that you must fill in before
                using it.
              </Text>
              {googleSheetsNodeIds.length > 0 && autoCreateGoogleSheets ? <>
                <VStack align='stretch'>
                  <Text mb='2'>
                    This template uses some Google Sheets blocks. Unless disabled, we will create all the necessary Google Sheets automatically for you. <br />This will require you to connect your Google Sheets account.
                  </Text>
                  <HStack justify='end'>
                    <Button
                      variant='ghost'
                      colorScheme='red'
                      onClick={() => setAutoCreateGoogleSheets(false)}>
                      Disable
                    </Button>
                    <Button
                      colorScheme='green'
                      onClick={createSheets}>
                      Create Sheets automatically (Recommended)
                    </Button>
                  </HStack>
                </VStack></> :
                <JSONForm
                  uiSchema={uiSchema}
                  initialData={initialData}
                  schema={{
                    type: "object",
                    properties: templateAndOverrides.overrides,
                  }}
                  onSubmit={(formData) =>
                    createFlowFromTemplate(templateId, formData)
                  }
                />
              }
            </>
          )}
        </VStack>
      </Modal>
      <SubscribeModal
        isOpen={isSubscribeOpen}
        onOpen={onSubscribeOpen}
        onClose={() => {
          onSubscribeClose();
        }}
        onSuccess={() => {
          onSubscribeClose();
        }}
      />
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={customNodes || {}}
          fitView
        >
          <Controls position="bottom-right" />
          <Background />
          {templateAndOverrides?.template && (
            <NameDescPanel
              defaultIsOpen
              title='Template'
              data={{
                name: templateAndOverrides?.template.name,
                desc: templateAndOverrides?.template.desc
              }}
              onUpdate={isOwner ? ((data) => TEMPLATES_API.updateTemplateApiV1TemplatesTemplateIdPut(templateId, data).then(
                (r) => setTemplateAndOverrides(r.data))) : null}
            >
              <HStack w='full' justify='end' mt='2'>
                <UseTemplateButton />
              </HStack>
            </NameDescPanel>
          )}
          <Panel position="top-center">
            <HStack
              p={4}
              boxShadow="xl"
              borderWidth='1px'
              borderColor=''
              rounded="10"
              bg="white"
              divider={<StackDivider />}
            >
              <Button
                id="validate-btn"
                isDisabled={isLoading}
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href.replace("/explore", "/gallery"));
                  toast({
                    title: "Link copied to clipboard.",
                    description: "Share it with anyone!",
                    status: "success",
                    duration: 2000,
                    isClosable: true,
                  });
                }}
                leftIcon={<MdShare />}
                colorScheme="brand"
                variant='ghost'
                borderWidth='2px'
              >
                Share
              </Button>
              <UseTemplateButton />
            </HStack>
          </Panel>
        </ReactFlow>
      </ReactFlowProvider>
    </Box >
  );
};
export default ExploreEdit;
